import React from 'react';
import { StatTile, Note, SubHeading, StatusPill, Chip } from './ui';
import ChartFrame from '../charts/ChartFrame';
import BarChart from '../charts/BarChart';
import { fmtNum, fmtDate, fmtPct, safeArray, isNum, DASH } from '../format';
import { SLOPE_BANDS, DEFAULT_MAX_SLOPE_PERCENT, bandRangeLabel, bandFallLabel, steeperShare } from '../slopeBands';

const FT = 3.28084;

export function slopeTone(percent) {
  if (!isNum(percent)) return 'neutral';
  if (percent < 10) return 'good';
  if (percent < 15) return 'warning';
  return 'critical';
}

/**
 * Parcel terrain from USGS 3DEP (1 m lidar where available): the elevation
 * envelope, slope and aspect, where the low and high points sit, and how the
 * elevations are distributed.
 */
export default function TerrainSection({ terrain, status, maxSlopePercent }) {
  if (!terrain && !status) return null;
  if (!terrain) {
    const label = status?.status === 'not_available' ? 'Elevation service not available.'
      : status?.status === 'not_found' ? 'No elevation samples came back for this footprint.'
        : 'Elevation lookup did not return data.';
    return (
      <>
        <SubHeading right={<StatusPill status={status} />}>Parcel terrain</SubHeading>
        <Note tone="muted">{label}{safeArray(status?.warnings).length ? ` ${status.warnings.join(' ')}` : ''}</Note>
      </>
    );
  }

  const stats = terrain.stats || {};
  const source = terrain.source || {};
  const footprint = terrain.footprint || {};
  const extremes = terrain.extremes || {};
  const signals = safeArray(terrain.signals);
  const histogram = safeArray(terrain.histogram);
  const acquisition = safeArray(source.acquisition_dates);
  const resolution = isNum(source.resolution_m) ? source.resolution_m : null;
  const resolutionLabel = resolution === null ? DASH : resolution <= 1.05 ? '1 m lidar' : `${fmtNum(resolution, resolution < 10 ? 1 : 0)} m`;
  const bandSummary = terrain.slope_bands || null;
  const bandRows = bandSummary ? SLOPE_BANDS.map((b) => ({ ...b, ...(safeArray(bandSummary.bands).find((x) => x.key === b.key) || {}) })) : [];
  const maxSlope = Number(maxSlopePercent) || DEFAULT_MAX_SLOPE_PERCENT;
  const overShare = steeperShare(terrain.samples, maxSlope);

  return (
    <>
      <SubHeading right={status && status.status !== 'ok' ? <StatusPill status={status} /> : null}>Parcel terrain</SubHeading>
      <div className="le-chip-row">
        <Chip>{footprint.kind === 'parcel' ? 'Sampled inside the parcel outline' : 'No parcel outline — 100 m square around the point'}</Chip>
        <Chip>{resolutionLabel}</Chip>
        {isNum(source.sample_count) ? <Chip>{fmtNum(source.sample_count)} samples · ~{fmtNum(footprint.spacing_m, 1)} m apart</Chip> : null}
        <Chip title="The 3D view renders USGS 1 m lidar terrain at true scale across the whole area; vertical exaggeration is a map setting and always stated">3D terrain true scale</Chip>
        {isNum(terrain.contours?.line_count) ? <Chip title="Smoothed contour lines traced through the elevation grid at the stated interval; on the map the highlight sweeps from the high point to the low point">{fmtNum(terrain.contours.line_count)} contour lines · {terrain.contours.interval_ft} ft interval</Chip> : null}
      </div>

      <div className="le-stat-grid">
        <StatTile label="Low point" value={isNum(stats.min_ft) ? `${fmtNum(stats.min_ft, 1)} ft` : DASH} sub={extremes.low ? 'Marked ▼ on the map' : null} />
        <StatTile label="High point" value={isNum(stats.max_ft) ? `${fmtNum(stats.max_ft, 1)} ft` : DASH} sub={extremes.high ? 'Marked ▲ on the map' : null} />
        <StatTile label="Relief" value={isNum(stats.relief_ft) ? `${fmtNum(stats.relief_ft, 1)} ft` : DASH} sub="High minus low" emphasis />
        <StatTile label="Overall slope" value={isNum(stats.slope_percent) ? fmtPct(stats.slope_percent, 1) : DASH} sub={stats.aspect_label ? `Falls to the ${stats.aspect_label}${isNum(stats.slope_degrees) ? ` · ${fmtNum(stats.slope_degrees, 1)}°` : ''}` : 'Plane fit across all samples'} delta={isNum(stats.slope_percent) ? { label: stats.slope_class || '', tone: slopeTone(stats.slope_percent) === 'good' ? 'flat' : 'down' } : null} />
        <StatTile label="Steepest spot" value={isNum(stats.max_local_slope_percent) ? fmtPct(stats.max_local_slope_percent, 1) : DASH} sub={isNum(footprint.spacing_m) ? `Between neighboring samples ${fmtNum(footprint.spacing_m, 1)} m apart · single step, often a wall or bank` : 'Between neighboring samples'} />
        <StatTile label="Mean elevation" value={isNum(stats.mean_ft) ? `${fmtNum(stats.mean_ft, 1)} ft` : DASH} sub={isNum(stats.std_ft) ? `±${fmtNum(stats.std_ft, 1)} ft spread` : null} />
      </div>

      {signals.length ? (
        <div className="le-signal-list">
          {signals.map((s) => <Note key={s.code} tone={s.tone === 'good' ? 'info' : s.tone || 'info'}>{s.message}</Note>)}
        </div>
      ) : null}

      {bandRows.length ? (
        <>
          <SubHeading>Slope bands</SubHeading>
          <div className="le-slope-bands" role="table" aria-label="Share of the parcel in each slope band">
            {bandRows.map((b) => (
              <div key={b.key} className="le-slope-band" role="row" title={`${b.label} ${bandRangeLabel(b)} · ${bandFallLabel(b)} of fall across a 50 ft deep house · ${b.buildability}`}>
                <span className="le-slope-band-swatch" style={{ background: b.color }} aria-hidden="true" />
                <span className="le-slope-band-name"><span>{b.label} · {bandRangeLabel(b)}</span><small>{b.buildability}</small></span>
                <span className="le-slope-band-bar" aria-hidden="true"><span style={{ width: `${Math.round((isNum(b.share) ? b.share : 0) * 100)}%`, background: b.color }} /></span>
                <span className="le-slope-band-share">{isNum(b.share) ? fmtPct(b.share * 100, b.share > 0 && b.share < 0.01 ? 1 : 0) : DASH}</span>
              </div>
            ))}
          </div>
          {isNum(overShare) ? (
            <p className="le-slope-verdict">
              {overShare > 0
                ? <><strong>{fmtPct(overShare * 100, overShare < 0.01 ? 1 : 0)} of the parcel is steeper than {maxSlope}%</strong>, your max buildable slope — marked solid red (white hatch) on the map in Slope view.</>
                : <>No part of the parcel is steeper than {maxSlope}%, your max buildable slope.</>}
              {isNum(bandSummary.median_percent) ? ` Median cell slope ${fmtPct(bandSummary.median_percent, 1)}` : ''}{isNum(bandSummary.p90_percent) ? `, 90th percentile ${fmtPct(bandSummary.p90_percent, 1)}` : ''}{isNum(bandSummary.smoothing_sigma_m || bandSummary.smoothing_m) ? ` · planning grade smoothed with a ${fmtNum(bandSummary.smoothing_sigma_m || bandSummary.smoothing_m, 0)} m Gaussian${isNum(bandSummary.smoothing_window_m) ? ` (about a ${fmtNum(bandSummary.smoothing_window_m, 0)} m window)` : ''}.` : '.'}{isNum(bandSummary.local_p99_percent) ? ` Steepest local grade (99th percentile, banks and ditches included) ${fmtPct(bandSummary.local_p99_percent, 1)}.` : ''}
            </p>
          ) : null}
        </>
      ) : null}

      {histogram.length ? (
        <ChartFrame
          subtitle="How the sampled elevations are distributed across the footprint"
          table={{
            columns: [
              { key: 'from_ft', label: 'From (ft)', align: 'right', format: (v) => fmtNum(v, 1) },
              { key: 'to_ft', label: 'To (ft)', align: 'right', format: (v) => fmtNum(v, 1) },
              { key: 'count', label: 'Samples', align: 'right', format: (v) => fmtNum(v) },
            ],
            rows: histogram,
          }}
        >
          <BarChart
            categories={histogram.map((b) => `${fmtNum(b.from_ft, 1)}`)}
            series={[{ id: 'samples', label: 'Samples', color: 'var(--le-ramp-3)', values: histogram.map((b) => b.count) }]}
            yFormat={(v) => fmtNum(v)}
            height={150}
          />
        </ChartFrame>
      ) : null}

      <div className="le-footnotes muted">
        Source: USGS 3D Elevation Program bare-earth DEM{source.projects?.length ? ` · ${source.projects.join(', ')}` : ''}{acquisition.length ? ` · flown ${acquisition.map((d) => fmtDate(d)).join(', ')}` : ''}{source.vertical_datum ? ` · ${source.vertical_datum}` : ''}. Elevations are bare earth (vegetation and structures removed); 1 ft ≈ {(1 / FT).toFixed(3)} m.
      </div>
    </>
  );
}
